/**
 * The València district view: which metrics it offers, and how it reads them.
 *
 * Ported from frontend/legacy/index.html, where the metric list, the ranking
 * sort and the hover card's letter breakdown were three places that each read
 * `properties` their own way. They are one module here because they have to
 * agree: the ranking's order, the choropleth's ramp and the legend's break
 * values all describe the same metric, and the legacy page's `lowFirst` bug —
 * a ranking sorted one way and painted the other — was possible only because
 * they were separate.
 *
 * The classification itself lives in lib/choropleth.ts; this module supplies
 * what to classify.
 */

import type { Feature } from "geojson";

import { fmt, fmtPct } from "./format";
import type { Letter } from "./letters";
import { LETTERS } from "./letters";
import { count, num, str } from "./props";
import type { Vista } from "./vista";

export { count, num };

/** Properties are flattened by backend/distritos.py, not nested. */
export type DistritoProps = Record<string, unknown>;

export interface Metric {
  key: string;
  label: string;
  format: (value: number | null) => string;
  /**
   * True when the metric's *low* end is the one demanding attention, which
   * inverts both the ramp and the ranking.
   *
   * Income is the only one. For every other metric a bigger number is the more
   * notable case, so it takes the darkest tone and ranks first; the poorest
   * districts are the ones to look at, and without this the page would say dark
   * = worst on eight metrics and dark = best on the ninth.
   */
  lowFirst?: boolean;
}

const euro = (v: number | null) => fmt(v == null ? v : Math.round(v), 0, "€");
const kwh = (v: number | null) => fmt(v, 1, "kWh/m²");

export const DISTRITO_METRICS: readonly Metric[] = [
  { key: "pct_2030", label: "% edificios F–G (obligación 2030)", format: fmtPct },
  {
    key: "renta_neta_hogar",
    label: "Renta neta media por hogar (2021)",
    format: euro,
    lowFirst: true,
  },
  { key: "pct_2033", label: "% edificios E (obligación 2033)", format: fmtPct },
  { key: "ahorro_por_vivienda", label: "Ahorro potencial por vivienda", format: euro },
  { key: "demanda_calef_kwh_m2", label: "Demanda de calefacción", format: kwh },
  { key: "demanda_refrig_kwh_m2", label: "Demanda de refrigeración", format: kwh },
  { key: "pct_geo_mejor", label: "% edificios donde geotermia gana (LCOE)", format: fmtPct },
  { key: "n_viviendas", label: "Nº de viviendas", format: (v) => fmt(v) },
  { key: "total_edificios", label: "Nº de edificios catastrales", format: (v) => fmt(v) },
] as const;

export const DEFAULT_METRIC = DISTRITO_METRICS[0] as Metric;

/**
 * The metrics a view can actually answer.
 *
 * Income is the one figure with no boundary below the district: the INE's Atlas
 * publishes by district and census section, never by barrio (DATOS §D). Offering
 * it in the barrio view would either show nineteen values spread over
 * eighty-eight areas or nothing at all, and the first is worse — a figure
 * attributed to an area it was not measured for reads exactly like one that was.
 */
export function metricsFor(vista: Vista): readonly Metric[] {
  return vista === "barrios"
    ? DISTRITO_METRICS.filter((m) => m.key !== "renta_neta_hogar")
    : DISTRITO_METRICS;
}

/**
 * Keeps a selection legal across a view change, rather than painting nothing.
 *
 * Null passes through: "no metric" is a choice the reader made — outlines only,
 * no choropleth — and silently replacing it with a metric on the next view
 * change would repaint a map they had deliberately cleared.
 */
export function metricForVista(
  metric: Metric | null,
  vista: Vista,
): Metric | null {
  if (metric === null) return null;
  const allowed = metricsFor(vista);
  return allowed.includes(metric) ? metric : (allowed[0] as Metric);
}

export interface Ranked {
  /** Districts carrying a value, in the metric's own order. */
  rows: DistritoProps[];
  /** Those with no value, which are listed but never ranked. */
  missing: DistritoProps[];
  /** Largest value present, so bars stay proportional under either direction. */
  max: number;
}

/**
 * The ranking behind the district list.
 *
 * Districts with no value are separated rather than sorted to the bottom: a
 * null is not a low score, and putting it at position 19 asserts a reading the
 * data does not support.
 */
export function rankDistritos(
  features: readonly Feature[],
  metric: Metric | null,
): Ranked {
  const props = features.map((f) => (f.properties ?? {}) as DistritoProps);

  // With no metric there is nothing to rank by, so the list becomes an index:
  // every area, in alphabetical order, still clickable. Keeping the previous
  // metric's order would show a ranking whose basis is no longer on screen.
  if (metric === null) {
    return {
      rows: [...props].sort((a, b) => areaName(a).localeCompare(areaName(b), "es")),
      missing: [],
      max: 1,
    };
  }

  const rows = props.filter((p) => num(p[metric.key]) != null);
  const missing = props.filter((p) => num(p[metric.key]) == null);

  const dir = metric.lowFirst ? 1 : -1;
  rows.sort((a, b) => dir * ((num(a[metric.key]) ?? 0) - (num(b[metric.key]) ?? 0)));

  // Bars stay proportional to the largest value regardless of sort direction,
  // so an inverted ranking shows short bars at the top rather than a misleading
  // full-width bar on the smallest number.
  const max = rows.reduce((mx, p) => Math.max(mx, num(p[metric.key]) ?? 0), 0);
  return { rows, missing, max: max || 1 };
}

/**
 * The area's own name.
 *
 * The two layers name themselves differently, and for a good reason: a district
 * feature's `distrito` IS its name, while a barrio feature carries `nombre` for
 * itself and `distrito` for the district it belongs to — which is the context a
 * reader needs to place "El Carme". Reading `distrito` for both is how every
 * label on the barrio map came out saying "Ciutat Vella".
 */
export function areaName(props: DistritoProps): string {
  return str(props["nombre"]) ?? str(props["distrito"]) ?? "–";
}

/** True for a feature from the barrio layer, which alone names itself. */
export function isBarrio(props: DistritoProps): boolean {
  return str(props["nombre"]) != null;
}

/**
 * The line under the name: where this area sits.
 *
 * A barrio is placed by its district, which is what someone who does not know
 * the city needs; a district is placed by its official number, which is what
 * appears on every municipal document.
 */
export function areaContext(props: DistritoProps): string {
  const cod = str(props["cod"]) ?? "–";
  return isBarrio(props)
    ? `${str(props["distrito"]) ?? "València"} · ${cod}`
    : `Distrito ${cod}`;
}

export interface LetterShare {
  /** Typed as the scale, not as a string: the callers index the colour maps. */
  letter: Letter;
  dwellings: number;
  pct: number;
}

/**
 * The A–G split for one district, weighted by DWELLINGS rather than buildings.
 *
 * A single G-rated tower houses hundreds of households while a G-rated garage
 * houses none, and a building-count share flattens that difference away. The
 * strip and the percentages read from the same denominator so they cannot tell
 * different stories.
 *
 * Returns an empty list when the district has no rated dwellings at all, which
 * is the caller's signal to omit the block rather than draw an empty bar.
 */
export function letterShares(props: DistritoProps): LetterShare[] {
  const shares = LETTERS.map((letter) => ({
    letter,
    dwellings: count(props[`viv_${letter}`]),
    pct: count(props[`pctviv_${letter}`]),
  }));
  return shares.some((s) => s.dwellings > 0) ? shares : [];
}

/** Total rated dwellings, the denominator the shares are quoted against. */
export function ratedDwellings(props: DistritoProps): number {
  return LETTERS.reduce((total, l) => total + count(props[`viv_${l}`]), 0);
}

/** The city-wide income the district figure is compared against. */
export interface RentaMeta {
  municipio_renta_neta_hogar?: number | null;
  ejercicio?: string | number | null;
}

/**
 * A district's income against the city average, as a signed percentage.
 *
 * Null when either side is missing — the card then prints the figure with no
 * comparison rather than an invented 0%.
 */
export function rentaDelta(
  districtIncome: number | null,
  cityIncome: number | null | undefined,
): number | null {
  if (districtIncome == null || cityIncome == null || cityIncome === 0) return null;
  return (100 * (districtIncome - cityIncome)) / cityIncome;
}
